import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useParams } from 'react-router'

import { type OnboardingPage } from '../routes'
import { track } from '../../utils/analytics'
import { OnboardingTitle } from '../components/OnboardingTitle'
import { OnboardingNextButton } from '../components/OnboardingNextButton'
import { useQuotation } from '../context'
import { useOnboardingWorkflow } from '../workflow'
import { TipsCard } from '../components/TipsCard'

import { RadioGroup, Hint } from '@olino/design-system'
import { CompanySizeLabel, PaymentFrequency } from '@olino/utils'

// This is a duplicate of the WelcomePage component in src/pages/onboarding/00-Welcome/Welcome.tsx. The goal is to show how to use the new layout in the onboarding flow.
// When the new layout is ready, we will solely use this WelcomePage and remove the one in src/pages/onboarding/00-Welcome/Welcome.tsx.

const options = [
  CompanySizeLabel.Tpe,
  CompanySizeLabel.Pme,
  CompanySizeLabel.Se,
  CompanySizeLabel.Eti,
] as const

const OptionTPE = () => (
  <div className="flex items-center gap-2">
    <span>TPE / Très petites strutures</span>
    <Hint>10 salariés, petite collectivité, artisan, association</Hint>
  </div>
)

const OptionPME = () => (
  <div className="flex items-center gap-2">
    <span>PME / Collectivité moyenne</span>
    <Hint>10 à 49 salariés</Hint>
  </div>
)

const OptionSE = () => (
  <div className="flex items-center gap-2">
    <span>Structure étendue</span>
    <Hint>50 à 249 salariés</Hint>
  </div>
)

const OptionETI = () => (
  <div className="flex items-center gap-2">
    <span>ETI</span>
    <Hint>250 à 4 999 salariés</Hint>
  </div>
)

const schema = z.object({
  companySizeLabel: z.enum(options, { required_error: 'Ce champ est requis' }),
})

type FormInputs = z.infer<typeof schema>

const Main = () => {
  const { updateQuotation } = useQuotation()
  const { proceed } = useOnboardingWorkflow()
  const { id } = useParams()

  const { control, handleSubmit } = useForm<FormInputs>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      companySizeLabel: options[0],
    },
  })

  const onSubmit = handleSubmit((data) => {
    track({
      event: 'submitted_taille',
      quotation_id: id,
    })

    const companySizeObject = {}

    proceed(() =>
      updateQuotation({
        details: { ...companySizeObject },
      })
    )
  })

  useEffect(() => {
    track({
      event: 'visited_taille',
      quotation_id: id,
    })
  }, [id])

  return (
    <>
      <OnboardingTitle.H1>Quelle est la taille de votre&nbsp;collectivité?</OnboardingTitle.H1>

      <form className="space-y-8" onSubmit={onSubmit}>
        <OnboardingTitle.H2>Type de collectivité</OnboardingTitle.H2>

        <div>
          <Controller
            control={control}
            name="companySizeLabel"
            render={({ field }) => (
              <RadioGroup
                options={options}
                getOptionLabel={(option) => {
                  switch (option) {
                    case CompanySizeLabel.Tpe:
                      return <OptionTPE />
                    case CompanySizeLabel.Pme:
                      return <OptionPME />
                    case CompanySizeLabel.Se:
                      return <OptionSE />
                    case CompanySizeLabel.Eti:
                      return <OptionETI />
                    default:
                      return ''
                  }
                }}
                value={field.value}
                onChange={field.onChange}
                direction="col"
                optionsContainerClassName="space-y-3"
                optionClassName="flex items-center"
              />
            )}
          />
        </div>

        <div className="flex justify-end">
          <OnboardingNextButton />
        </div>
      </form>
    </>
  )
}

const Tips = () => {
  return (
    <div className="space-y-4">
      <TipsCard
        title="Notre parcours en ligne"
        content={[
          "Merci de choisir la taille de votre collectivité, cela nous permettra d'établir un meilleur devis",
        ]}
      />
    </div>
  )
}

export default {
  main: <Main />,
  tips: <Tips />,
} satisfies OnboardingPage
